'use client';

import { siteConfigIds } from '@/constants/site-config-ids';
import { usePathname } from 'next/navigation';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';

// Define window interface extensions
declare global {
  interface Window {
    HerodashToken?: string;
    Herodash?: (...args: any[]) => void;
    HeroDashChatWidget?: {
      on: (
        event: string,
        callback: () => void,
      ) => {
        remoteReady: () => void;
      };
    };
  }
}

// Type definition for Herodash function
interface HerodashFunction {
  (...args: any[]): void;
  q?: any[];
}

const hiddenRoutes = ['/cart', '/checkout'];

/**
 * Custom hook to initialize and manage HeroChat widget
 * @param token - Authentication token for HeroChat
 */

export default function HeroDash() {
  const pathname = usePathname();
  const shouldHideChat = hiddenRoutes.includes(pathname);

  useEffect(() => {
    // Initialize Herodash if not already present
    if (!window.Herodash) {
      window.HerodashToken = siteConfigIds.heroDashToken;

      // Create script element
      const script = document.createElement('script');

      script.async = true;
      script.src = `https://guest.herodash.ai/chat.js?token=${siteConfigIds.heroDashToken}&timesMap=${Math.random()}`;

      // Initialize queue function
      window.Herodash = function () {
        ((window.Herodash as HerodashFunction).q = (window.Herodash as HerodashFunction).q || []).push(arguments);
      };

      // Append script to document head
      document.head.appendChild(script);
    }

    // Initialize widget when script loads
    const initializeWidget = () => {
      if (window.HeroDashChatWidget) {
        window.HeroDashChatWidget.on('load', () => {
          const chatIframe = document.getElementById('heroDash-chat-widget-iframe');

          if (chatIframe) {
            chatIframe.title = 'chat-widget';
          }
        }).remoteReady();
      }
    };

    // Add load event listener
    document.querySelector('script[src*="herodash.ai/chat.js"]')?.addEventListener('load', initializeWidget);

    // Cleanup
    return () => {
      document.querySelector('script[src*="herodash.ai/chat.js"]')?.removeEventListener('load', initializeWidget);
    };
  }, []);

  if (shouldHideChat) {
    return createPortal(<style>{'#heroDash-chat-widget-iframe{display:none!important}'}</style>, document.head);
  }

  return createPortal(<style>{'#heroDash-chat-widget-iframe{z-index:100!important}'}</style>, document.head);
}
